import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

const { black, dark, primary, light } = colors;
const { borderWidth } = borders;

function MiniStatisticsCard({ bgColor, title, count, percentage, icon, direction }) {
  return (
    <Card
      sx={{
        px: "16px",
        py: "12px",
        borderRadius: "18px",
        background: light.main,
        border: `${borderWidth[1]} solid ${rgba(black.main, 0.25)}`,
        boxShadow: "none",
        transition: "all 0.2s ease",

        "&:hover": {
          transform: "translateY(-2px)",
          background: light.secondary,
          border: `${borderWidth[1]} solid ${rgba(primary.main, 0.18)}`,
        },
      }}
    >
      <Grid container alignItems="center" spacing={1}>
        {direction === "left" && (
          <Grid item>
            <VuiBox
              width="3rem"
              height="3rem"
              borderRadius="14px"
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                color: primary.main,
                background: light.secondary,
                border: `${borderWidth[1]} solid ${rgba(primary.main, 0.16)}`,
              }}
            >
              {icon.component}
            </VuiBox>
          </Grid>
        )}

        <Grid item xs={8}>
          <VuiBox ml={direction === "left" ? 2 : 0} lineHeight={1}>
            <VuiTypography
              variant="caption"
              color="dark"
              fontWeight={title.fontWeight}
              sx={{
                opacity: 0.7,
                textTransform: "capitalize",
              }}
            >
              {title.text}
            </VuiTypography>

            <VuiTypography
              display="block"
              variant="subtitle1"
              color="dark"
              fontWeight="bold"
              sx={{ mt: 0.4 }}
            >
              {count}

              {percentage?.text && (
                <VuiTypography
                  variant="caption"
                  color={percentage.color}
                  fontWeight="regular"
                  ml={0.4}
                >
                  {percentage.text}
                </VuiTypography>
              )}
            </VuiTypography>
          </VuiBox>
        </Grid>

        {direction === "right" && (
          <Grid item xs={4}>
            <VuiBox
              width="3rem"
              height="3rem"
              marginLeft="auto"
              borderRadius="14px"
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                color: primary.main,
                background: light.secondary,
                border: `${borderWidth[1]} solid ${rgba(primary.main, 0.16)}`,
              }}
            >
              <Icon fontSize="small" color="inherit">
                {icon.component}
              </Icon>
            </VuiBox>
          </Grid>
        )}
      </Grid>
    </Card>
  );
}

MiniStatisticsCard.defaultProps = {
  bgColor: "white",
  title: {
    fontWeight: "medium",
    text: "",
  },
  percentage: {
    color: "success",
    text: "",
  },
  direction: "right",
};

MiniStatisticsCard.propTypes = {
  bgColor: PropTypes.oneOf([
    "white",
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  title: PropTypes.shape({
    fontWeight: PropTypes.oneOf(["light", "regular", "medium", "bold"]),
    text: PropTypes.string,
  }),
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  icon: PropTypes.shape({
    color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
    component: PropTypes.node.isRequired,
  }).isRequired,
  direction: PropTypes.oneOf(["right", "left"]),
};

export default MiniStatisticsCard;
